import { useCallback } from 'react';
import { useNotification } from '@/hooks';
import { NotificationSeverity } from '@/components/notifications/Notification';
import { useLazyGetShippingLabelBase64Query } from '../services';
import type { LabelProvider } from '../services/shippings.service';
import { ShippingListApi } from '../models';

const toArray = <T>(v: T | T[]): T[] => (Array.isArray(v) ? v : [v]);

const base64ToBlob = (b64: string): Blob => {
  const bytes = atob(b64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: 'application/pdf' });
};

const printBlob = (blobUrl: string): Promise<void> =>
  new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.style.cssText =
      'position:fixed;right:0;bottom:0;width:0;height:0;border:0';

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      setTimeout(() => {
        iframe.remove();
        URL.revokeObjectURL(blobUrl);
      }, 60_000);
      resolve();
    };

    iframe.onload = () => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.addEventListener('afterprint', finish);
      iframe.contentWindow?.print();
      setTimeout(finish, 30_000);
    };
    iframe.src = blobUrl;
    document.body.appendChild(iframe);
  });

const downloadBlob = (blobUrl: string, fileName: string) => {
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
};

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const usePrintLabel = () => {
  const { showNotification } = useNotification();
  const [fetchLabel, { isLoading: isPrinting }] =
    useLazyGetShippingLabelBase64Query();
  const [fetchLabelForDownload, { isLoading: isDownloading }] =
    useLazyGetShippingLabelBase64Query();

  const handlePrintLabel = useCallback(
    async (
      shipping: ShippingListApi,
      forceRegenerateLabel = false,
      labelProvider?: LabelProvider
    ) => {
      try {
        const result = await fetchLabel({
          shippingNumber: Number(shipping.number),
          forceRegenerateLabel,
          labelProvider,
        }).unwrap();
        const pdfs = toArray(result.pdfBase64);
        for (const b64 of pdfs) {
          // eslint-disable-next-line no-await-in-loop
          await printBlob(URL.createObjectURL(base64ToBlob(b64)));
        }
      } catch (error: any) {
        const errorMessage =
          error?.data?.message ||
          error?.error ||
          'Error al generar la etiqueta.';
        showNotification(errorMessage, NotificationSeverity.error);
      }
    },
    [fetchLabel, showNotification]
  );

  const handleRegenerateLabel = useCallback(
    (shipping: ShippingListApi) => handlePrintLabel(shipping, true),
    [handlePrintLabel]
  );

  const handleDownloadLabel = useCallback(
    async (shipping: ShippingListApi, labelProvider?: LabelProvider) => {
      try {
        const result = await fetchLabelForDownload({
          shippingNumber: Number(shipping.number),
          labelProvider,
        }).unwrap();
        const pdfs = toArray(result.pdfBase64);
        const fileNames = toArray(result.fileName);
        for (let index = 0; index < pdfs.length; index += 1) {
          const fileName =
            fileNames[index] ||
            fileNames[0] ||
            `etiqueta-${shipping.number}.pdf`;
          downloadBlob(URL.createObjectURL(base64ToBlob(pdfs[index])), fileName);
          if (index < pdfs.length - 1) {
            // eslint-disable-next-line no-await-in-loop
            await wait(400);
          }
        }
      } catch (error: any) {
        const errorMessage =
          error?.data?.message ||
          error?.error ||
          'Error al descargar la etiqueta.';
        showNotification(errorMessage, NotificationSeverity.error);
      }
    },
    [fetchLabelForDownload, showNotification]
  );

  return {
    handlePrintLabel,
    isPrinting,
    handleRegenerateLabel,
    handleDownloadLabel,
    isDownloading,
  };
};
